import { InMemoryCache, } from '@apollo/client';
import { getFullName } from '@provision/formatting-utils';
import { CachePersistor } from 'apollo3-cache-persist';
import localforage from 'localforage';
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { attachBroadcast } from './broadcast-cache';
let listeners = [];
let config = {};
const configStore = {
    updateConfig: (update) => {
        config = { ...config, ...update };
        emitChange();
    },
    subscribe: (listener) => {
        listeners = [...listeners, listener];
        return () => {
            listeners = listeners.filter((l) => l !== listener);
        };
    },
    getSnapshot: () => {
        return config;
    },
};
function emitChange() {
    for (const listener of listeners) {
        listener();
    }
}
export const useGraphQLConfig = (initialConfig) => {
    const initRef = useRef(initialConfig);
    const config = useSyncExternalStore(configStore.subscribe, configStore.getSnapshot);
    useEffect(() => {
        if (!initRef.current || !Object.keys(config || {}).length) {
            configStore.updateConfig(initialConfig);
            initRef.current = initialConfig;
        }
    }, [config, initialConfig]);
    return {
        config,
        updateConfig: configStore.updateConfig,
    };
};
// Initialize localforage cache driver.
localforage.config({
    name: 'graphql-cache',
    driver: localforage.INDEXEDDB,
});
export const partnerCaches = new Map();
export const getCache = (partnerId) => {
    const existing = partnerCaches.get(partnerId);
    if (existing)
        return existing;
    const cache = new InMemoryCache({
        typePolicies: {
            // Membership configuration product IDs are reused across segment-specific configs.
            // Keep these embedded under their parent response to prevent cross-segment cache collisions.
            AFConfigProduct: { keyFields: false },
            AFConfigProductPrice: { keyFields: false },
            // Mention options can legitimately repeat the same user id for multiple groups.
            // Disabling normalization preserves each row from the GraphQL response as-is.
            MentionOption: { keyFields: false },
            LabelAssignment: { keyFields: ['entityId', 'labelId'] },
            GetActivityFeedResponse: {
                keyFields: false,
            },
            OncallManagementSchedule: {
                fields: {
                    conditions: {
                        read: (existing) => {
                            // It should conform with DataConditionNode type.
                            try {
                                return JSON.parse(existing ?? null);
                            }
                            catch (e) {
                                console.error(e);
                                return null;
                            }
                        },
                    },
                },
            },
            Client: {
                fields: {
                    fullName: (_, { readField }) => {
                        const firstName = readField('firstName');
                        const lastName = readField('lastName');
                        return getFullName({
                            firstName,
                            lastName,
                        });
                    },
                    marketingOptOut: (existing) => {
                        return existing ?? false;
                    },
                    isDeleted: (_, { readField }) => {
                        return Boolean(readField('deletedAt'));
                    },
                },
            },
            Query: {
                fields: {
                    getPortalioProposals: {
                        keyArgs: false,
                        merge(existing, incoming) {
                            const nextRecordIds = new Set(incoming.records.map(({ __ref }) => __ref));
                            return {
                                records: [
                                    ...(existing?.records || []).filter(({ __ref }) => !nextRecordIds.has(__ref)),
                                    ...incoming.records,
                                ],
                                total: incoming.total,
                            };
                        },
                    },
                    getDtoolsProjects: {
                        keyArgs: ['params', ['searchValue', 'includeArchived']],
                        merge(existing, incoming) {
                            const nextRecordIds = new Set(incoming.projects.map(({ __ref }) => __ref));
                            return {
                                projects: [
                                    ...(existing?.projects || []).filter(({ __ref }) => !nextRecordIds.has(__ref)),
                                    ...incoming.projects,
                                ],
                                totalProjects: incoming.totalProjects,
                            };
                        },
                    },
                },
            },
        },
    });
    attachBroadcast(cache);
    void new CachePersistor({
        cache,
        storage: localforage,
        maxSize: false,
    }).persist();
    partnerCaches.set(partnerId, cache);
    return cache;
};
